if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.isVisible = false;
        this.observer = null;
        this.variantSubscription = null;
        this.hasScrolledPastButton = false; // Flag: l'utilisateur a-t-il déjà scrollé au-delà du bouton?
      }

      connectedCallback() {
        // 1. Observer le bouton principal pour show/hide
        this.setupIntersectionObserver();

        // 2. Écouter les changements de variante
        this.setupVariantListener();

        // 3. Cache elements
        this.cacheElements();

        // 4. Setup upsell integration
        this.setupUpsellIntegration();

        // 5. Sync with main form on load (handles preselected options like bundle-picker)
        this.syncWithMainForm();

        // 6. Observer le prix principal pour synchroniser automatiquement
        this.setupPriceObserver();
      }

      cacheElements() {
        this.variantInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonText = this.querySelector('.sticky-add-to-cart__button-text');
        this.priceContainer = this.querySelector('.sticky-add-to-cart__price');
        this.quantityInput = this.querySelector('.quantity__input');
        this.productForm = this.querySelector('product-form');
      }

      setupIntersectionObserver() {
        // Observer le bouton "Add to cart" principal
        // Essayer d'abord avec le sectionId, sinon chercher n'importe quel ProductSubmitButton
        let mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!mainButton) {
          // Fallback: chercher le premier bouton ProductSubmitButton sur la page
          mainButton = document.querySelector('[id^="ProductSubmitButton-"]');
        }

        if (!mainButton) {
          console.warn('Sticky Add to Cart: Main submit button not found');
          return;
        }

        // Extraire le vrai sectionId du bouton trouvé pour la synchronisation
        const buttonId = mainButton.id;
        const realSectionId = buttonId.replace('ProductSubmitButton-', '');
        if (realSectionId !== this.sectionId) {
          this.sectionId = realSectionId;
          this.dataset.sectionId = realSectionId;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const buttonTop = entry.boundingClientRect.top;
              const headerOffset = 80; // Account for sticky header

              // Le bouton est "scrollé passé" si son TOP est AU-DESSUS du viewport (négatif ou sous le header)
              const isButtonScrolledPast = buttonTop < headerOffset;

              if (entry.isIntersecting) {
                // Le bouton est visible dans le viewport
                this.hide();
              } else {
                // Le bouton n'est pas visible
                // On montre le sticky SEULEMENT si le bouton est AU-DESSUS du viewport
                // (l'utilisateur a scrollé vers le bas et l'a dépassé)
                // PAS si le bouton est EN-DESSOUS (page vient de charger, bouton sous le fold)
                if (isButtonScrolledPast) {
                  this.show();
                }
              }
            });
          },
          {
            threshold: 0,
            rootMargin: '-80px 0px 0px 0px', // Account for sticky header
          }
        );

        this.observer.observe(mainButton);
      }

      setupVariantListener() {
        // S'abonner aux changements de variante via pub/sub
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantSubscription = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }
      }

      onVariantChange({ data }) {
        // Mettre à jour seulement si c'est la même section
        if (data.sectionId !== this.sectionId) return;

        const { variant, html } = data;

        // 1. Mettre à jour l'input hidden du variant
        this.updateVariantId(variant?.id);

        // 2. Mettre à jour le prix depuis le bundle-picker sélectionné
        this.updatePrice(html);

        // 3. Mettre à jour l'état du bouton
        this.updateButtonState(variant);

        // 4. Mettre à jour les règles de quantité
        this.updateQuantityRules(html);
      }

      updateVariantId(variantId) {
        if (this.variantInput) {
          this.variantInput.value = variantId || '';
        }
      }

      updatePrice(html) {
        if (!this.priceContainer) return;

        // Priorité 1: récupérer le prix depuis la card sélectionnée du bundle-picker
        const bundlePicker = document.querySelector('[data-bundle-picker]');
        if (bundlePicker) {
          const selectedCard = bundlePicker.querySelector('.bundle-picker__card--selected');
          const bundlePrice = selectedCard?.querySelector('.bundle-picker__price');
          if (bundlePrice) {
            const priceText = bundlePrice.textContent.trim();
            const isSalePrice = bundlePrice.hasAttribute('data-sale-price');
            const comparePrice = selectedCard?.querySelector('.bundle-picker__compare-price');
            const comparePriceText = comparePrice?.textContent?.trim();

            let priceHtml = `
              <div class="price">
                <div class="price__container">
                  <div class="price__regular">
                    <span class="visually-hidden visually-hidden--inline">${window.variantStrings?.regularPrice || 'Prix habituel'}</span>
                    <span class="price-item price-item--regular${isSalePrice ? ' sticky-atc-sale-price' : ''}">${priceText}</span>
                    ${comparePriceText ? `<span class="sticky-atc-compare-price">${comparePriceText}</span>` : ''}
                  </div>
                </div>
              </div>
            `;
            this.priceContainer.innerHTML = priceHtml;
            return;
          }
        }

        // Fallback: récupérer depuis le HTML retourné par le serveur
        if (html) {
          const newPriceElement = html.getElementById(`price-${this.sectionId}`);
          if (newPriceElement) {
            this.priceContainer.innerHTML = newPriceElement.innerHTML;
            return;
          }
        }

        // Dernier fallback: récupérer le prix directement depuis le DOM de la page
        let mainPriceElement = document.getElementById(`price-${this.sectionId}`);
        if (!mainPriceElement) {
          const productInfo = document.querySelector('product-info');
          mainPriceElement = productInfo?.querySelector('[id^="price-"]');
        }
        if (!mainPriceElement) {
          mainPriceElement = document.querySelector('[id^="price-"]:not([id^="sticky-price"])');
        }
        if (mainPriceElement) {
          this.priceContainer.innerHTML = mainPriceElement.innerHTML;
        }
      }

      updateButtonState(variant) {
        if (!this.submitButton || !this.buttonText) return;

        if (!variant) {
          // Variante non disponible
          this.submitButton.disabled = true;
          this.buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
        } else if (!variant.available) {
          // En rupture de stock
          this.submitButton.disabled = true;
          this.buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else {
          // Disponible
          this.submitButton.disabled = false;
          this.buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      updateQuantityRules(html) {
        if (!this.quantityInput || !html) return;

        // Récupérer les règles de quantité depuis le HTML
        const quantityForm = html.getElementById(`Quantity-Form-${this.sectionId}`);
        if (!quantityForm) return;

        const newQuantityInput = quantityForm.querySelector('.quantity__input');
        if (!newQuantityInput) return;

        // Mettre à jour les attributs min/max/step
        const min = newQuantityInput.getAttribute('min');
        const max = newQuantityInput.getAttribute('max');
        const step = newQuantityInput.getAttribute('step');

        if (min) this.quantityInput.setAttribute('min', min);
        if (max) {
          this.quantityInput.setAttribute('max', max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
        if (step) this.quantityInput.setAttribute('step', step);

        // Réinitialiser la valeur au minimum
        this.quantityInput.value = min || 1;
      }

      syncWithMainForm() {
        // Wait for bundle-picker and other pickers to initialize and trigger their sync
        // Then sync sticky with the main form's current variant
        setTimeout(() => {
          // Chercher le formulaire principal
          let mainForm = document.querySelector(`#product-form-${this.sectionId}`);
          if (!mainForm) {
            // Fallback: chercher dans product-info
            const productInfo = document.querySelector('product-info');
            mainForm = productInfo?.querySelector('form[action*="/cart/add"]');
          }
          if (!mainForm) return;

          const mainVariantInput = mainForm.querySelector('input[name="id"]');
          if (mainVariantInput && this.variantInput) {
            const mainVariantId = mainVariantInput.value;
            if (mainVariantId && mainVariantId !== this.variantInput.value) {
              this.variantInput.value = mainVariantId;

              // Also update button state based on main form
              let mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
              if (!mainSubmitButton) {
                mainSubmitButton = document.querySelector('[id^="ProductSubmitButton-"]');
              }
              if (mainSubmitButton && this.submitButton && this.buttonText) {
                this.submitButton.disabled = mainSubmitButton.disabled;
                this.buttonText.textContent = mainSubmitButton.querySelector('[name="add"] > span:not(.loading__spinner)')?.textContent
                  || mainSubmitButton.textContent?.trim()
                  || this.buttonText.textContent;
              }

              // Update price - priorité au bundle-picker
              this.updatePrice(null);
            }
          }
        }, 200); // Wait for bundle-picker's 100ms delay + processing time
      }

      setupPriceObserver() {
        // Observer le prix principal pour synchroniser automatiquement le sticky ATC
        // Essayer de trouver l'élément prix avec le sectionId actuel
        let mainPriceElement = document.getElementById(`price-${this.sectionId}`);

        // Fallback: chercher n'importe quel élément price-* dans product-info
        if (!mainPriceElement) {
          const productInfo = document.querySelector('product-info');
          if (productInfo) {
            mainPriceElement = productInfo.querySelector('[id^="price-"]');
          }
        }

        // Dernier fallback: chercher globalement (exclure sticky-price)
        if (!mainPriceElement) {
          mainPriceElement = document.querySelector('[id^="price-"]:not([id^="sticky-price"])');
        }

        if (!mainPriceElement || !this.priceContainer) return;

        this.priceObserver = new MutationObserver(() => {
          // Copier le contenu du prix principal vers le sticky ATC
          this.priceContainer.innerHTML = mainPriceElement.innerHTML;
        });

        this.priceObserver.observe(mainPriceElement, {
          childList: true,
          subtree: true,
          characterData: true
        });

        // Sync initial
        this.priceContainer.innerHTML = mainPriceElement.innerHTML;
      }

      show() {
        if (this.isVisible) return;
        this.isVisible = true;
        this.classList.add('is-visible');
      }

      hide() {
        if (!this.isVisible) return;
        this.isVisible = false;
        this.classList.remove('is-visible');
      }

      setupUpsellIntegration() {
        const form = this.querySelector('form');
        if (!form) return;

        form.addEventListener('submit', async (evt) => {
          const checkedUpsells = this.getCheckedUpsellVariants();

          // If no upsells selected, let original handler run
          if (checkedUpsells.length === 0) {
            return;
          }

          // Prevent default and stop propagation to prevent product-form handler
          evt.preventDefault();
          evt.stopImmediatePropagation();

          if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

          // Show loading state
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');

          try {
            // Build items array for batch add
            const items = [];

            // Get main product info
            const mainVariantId = this.variantInput.value;
            const mainQuantity = this.quantityInput ? this.quantityInput.value : 1;

            // Add upsell products FIRST (they will appear after main product in cart)
            checkedUpsells.forEach((variantId) => {
              items.push({
                id: parseInt(variantId),
                quantity: 1,
              });
            });

            // Add main product LAST (it will appear first in cart)
            items.push({
              id: parseInt(mainVariantId),
              quantity: parseInt(mainQuantity),
            });

            // Make batch add request
            const response = await this.addItemsToCart(items);

            if (response.status) {
              // Error occurred
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-add-to-cart',
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            // Success
            const cart =
              document.querySelector('cart-notification') || document.querySelector('cart-drawer');

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-add-to-cart',
                productVariantId: mainVariantId,
                cartData: response,
              });
            }

            if (cart) {
              // Render cart sections manually
              this.renderCartSections(cart, response.sections);

              // Open the cart drawer
              cart.open();
            } else {
              window.location = window.routes.cart_url;
            }
          } catch (error) {
            console.error('Sticky add to cart with upsells error:', error);
          } finally {
            this.submitButton.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
          }
        }, true); // true = capture phase, runs before other listeners
      }

      getCheckedUpsellVariants() {
        // Find the upsell-products component in the main section
        const upsellComponent = document.querySelector(`upsell-products[data-section-id="${this.sectionId}"]`);
        if (!upsellComponent) return [];

        const checked = [];
        const checkboxes = upsellComponent.querySelectorAll('.upsell-products__checkbox');
        checkboxes.forEach((checkbox) => {
          if (checkbox.checked) {
            checked.push(checkbox.dataset.variantId);
          }
        });
        return checked;
      }

      async addItemsToCart(items) {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const body = { items };

        // Add sections for cart UI update
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });

        return response.json();
      }

      renderCartSections(cart, sections) {
        if (!sections) return;

        // Remove empty state
        const drawerInner = cart.querySelector('.drawer__inner');
        if (drawerInner) {
          drawerInner.classList.remove('is-empty');
        }
        cart.classList.remove('is-empty');

        // Render each section
        cart.getSectionsToRender().forEach((section) => {
          const html = sections[section.id];
          if (!html) return;

          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement) return;

          // Parse the HTML and extract the inner content
          const parsed = new DOMParser().parseFromString(html, 'text/html');

          // Try the section selector first, then .shopify-section, then body
          let content = null;
          if (section.selector) {
            content = parsed.querySelector(section.selector);
          }
          if (!content) {
            content = parsed.querySelector('.shopify-section');
          }
          if (!content) {
            content = parsed.body;
          }

          if (content) {
            sectionElement.innerHTML = content.innerHTML;
          }
        });

        // Re-attach overlay click handler
        const overlay = cart.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', cart.close.bind(cart));
        }
      }

      disconnectedCallback() {
        // Cleanup
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        if (this.variantSubscription) {
          this.variantSubscription();
          this.variantSubscription = null;
        }

        if (this.priceObserver) {
          this.priceObserver.disconnect();
          this.priceObserver = null;
        }
      }
    }
  );
}
